import { useEffect, useRef } from "react";
import {
  axisBottom,
  axisLeft,
  extent,
  line,
  scaleLinear,
  select,
} from "d3";
import { AnalysisLstatLogsGraphInterface } from "../../../interfaces/fe.interfaces";
import styles from "./styles.module.scss";
import AnalysisLstatTextWrap from "./AnalysisLstatTextWrap";

const MARGIN = { top: 30, right: 30, bottom: 50, left: 50 };

export interface DataPoint { x: number; y: number }
export interface AnalysisLstatConnectedScatterplotProps {
  width: number;
  height: number;
  data: DataPoint[];
  graph: AnalysisLstatLogsGraphInterface;
}

const AnalysisLstatConnectedScatterplot = ({
  width,
  height,
  data,
  graph
}: AnalysisLstatConnectedScatterplotProps) => {
  // bounds = area inside the graph axis = calculated by substracting the margins
  const axesRef = useRef(null);
  const boundsWidth = width - MARGIN.right - MARGIN.left;
  const boundsHeight = height - MARGIN.top - MARGIN.bottom;

  // Y axis
  const [, max] = extent(data, (d) => d.y);
  const yScale = scaleLinear()
    .domain([0, max || 0])
    .range([boundsHeight, 0]);

  // X axis
  const [, xMax] = extent(data, (d) => d.x);
  const xScale = scaleLinear()
    .domain([0, xMax || 0])
    .range([0, boundsWidth]);

  // Render the X and Y axis using d3.js, not react
  useEffect(() => {
    const svgElement = select(axesRef.current);
    svgElement.selectAll("*").remove();
    const xAxisGenerator = axisBottom(xScale);
    svgElement
      .append("g")
      .attr("transform", "translate(0," + boundsHeight + ")")
      .call(xAxisGenerator);

    const yAxisGenerator = axisLeft(yScale);
    svgElement.append("g").call(yAxisGenerator);
  }, [xScale, yScale, boundsHeight]);

  // Build the line
  const lineBuilder = line<DataPoint>()
    .x((d) => xScale(d.x))
    .y((d) => yScale(d.y));
  const linePath = lineBuilder(data);
  if (!linePath) {
    return null;
  }

  // Build the circles
  const allCircles = data.map((item, i) => {
    return (
      <circle
        key={i}
        cx={xScale(item.x)}
        cy={yScale(item.y)}
        r={4}
        fill={"#007dba"}
      />
    );
  });

  return (
    <div className={styles.wisaAnalysisLstatGraphConnectedScatterplot}>
      <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        >
          <path
            d={linePath}
            opacity={0.3}
            stroke="#007dba"
            fill="none"
            strokeWidth={2}
          />
          {allCircles}
        </g>
        <g
          width={boundsWidth}
          height={boundsHeight}
          ref={axesRef}
          transform={`translate(${[MARGIN.left, MARGIN.top].join(",")})`}
        />
        <text
          x={((boundsWidth - 10) / 2)}
          y={(boundsHeight + MARGIN.bottom + 15)}
          fontVariant="titling-caps">
          {graph.x_label}
        </text>
        <text
          x={(-height / 2)}
          y={(10)}
          style={{
            textAnchor: "middle",
          }}
          transform="rotate(-90)">
          {graph.y_label}
        </text>
        <text
          x={((boundsWidth + MARGIN.left + MARGIN.right) / 2)}
          y={(MARGIN.top - 15)}
          style={{textAnchor: "middle"}}
        >
          {graph.event_category[0]}
        </text>
      </svg>
      <svg
        viewBox={`0,0,${100},${height}`}
        width={100}
        height={height}
      >
        <text x={0} y={0}>
          <tspan
            x={0}
            dy={12}
          >
            Name:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            {graph.username}
          </tspan>
          <tspan
            x={0}
            dy={14}
          >
            Version:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            {graph.version}
          </tspan>
          <tspan
            x={0}
            dy={14}
          >
            Trial time:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            {graph.trial_test_time_date}
          </tspan>
          <tspan
            x={0}
            dy={14}
          >
            Notes:
          </tspan>
          <tspan
            x={0}
            dy={12}
          >
            <AnalysisLstatTextWrap text={graph.notes} width={100} />
          </tspan>
        </text>
      </svg>
    </div>
  );
};

export default AnalysisLstatConnectedScatterplot;